// Miky Motorsport - cista matematika AI ridice (rizeni, zakriveni, cilova rychlost).
// Bez THREE/Rapier - ciste aritmeticke funkce nad prostymi cisly/objekty, testovatelne
// v Node. ai.js je importuje a vola, aby chovani AI zustalo identicke.
import { clamp } from './physics/tires.js';

// Okna pro odhad zakriveni [d0, d1, d2] metru pred autem (blizko/stred/dal).
export const AI_CURV_WINDOWS = [[2, 9, 16], [8, 18, 28], [16, 30, 46]];

// Normalizace 2D vektoru (x,z). Degenerace (0,0) -> {0,0} (delitel orezan na 1).
export function norm(x, z) {
  const l = Math.hypot(x, z) || 1;
  return { x: x / l, z: z / l };
}

// Zakriveni z jednoho okna trojice bodu a->b->c: uhel mezi smery (a->b) a (b->c)
// pres acos(clamp(dot,-1,1)), deleno polovinou delky okna (seg). Kolinearni body
// (rovinka) -> 0. acos je NaN-guarded clampem dot do [-1,1]. seg<=0 -> 0 (ochrana).
export function curvatureFromTriple(a, b, c, seg) {
  if (!(seg > 0)) return 0;
  const u = norm(b.x - a.x, b.z - a.z);
  const w = norm(c.x - b.x, c.z - b.z);
  const ang = Math.acos(clamp(u.x * w.x + u.z * w.z, -1, 1));
  return ang / seg;
}

// MAX zakriveni pres sadu oken. samples = pole trojic [{a,b,c}, ...] (kazda trojice
// jsou 3 body na trati), segLengths = pole odpovidajicich seg (polovina delky okna).
// Vraci nejostrejsi zatacku v dohledu (kolinearni vsude -> 0).
export function curvatureFromSamples(samples, segLengths) {
  let curvature = 0;
  for (let i = 0; i < samples.length; i++) {
    const s = samples[i];
    curvature = Math.max(curvature, curvatureFromTriple(s.a, s.b, s.c, segLengths[i]));
  }
  return curvature;
}

// MAX zakriveni pres okna pred autem na dane trati. track.pointAtS(s) -> {x,z,...}.
// baseS = aktualni s na trati, windows = AI_CURV_WINDOWS (kazde [d0,d1,d2] metru).
export function curvatureFrom(track, baseS, windows = AI_CURV_WINDOWS) {
  let curvature = 0;
  for (const win of windows) {
    const a = track.pointAtS(baseS + win[0]);
    const b = track.pointAtS(baseS + win[1]);
    const c = track.pointAtS(baseS + win[2]);
    const seg = (win[2] - win[0]) / 2;
    curvature = Math.max(curvature, curvatureFromTriple(a, b, c, seg));
  }
  return curvature;
}

// Cilova rychlost (m/s) dle zakriveni. V zatacce v = sqrt(latAccel / curvature),
// kde lateralni zrychleni roste se skillem (lepsi AI projizdi zatacky rychleji).
// Zanedbatelne zakriveni (<=1e-4) -> rovinka -> maxStraightMs. Vysledek orezan
// stropem maxStraightMs (AI neprekroci top speed auta).
export function targetSpeedFor(curvature, skill, maxStraightMs) {
  const latAccel = 5.8 + skill * 4.0; // m/s^2
  const v = curvature > 1e-4 ? Math.sqrt(latAccel / curvature) : maxStraightMs;
  return Math.min(v, maxStraightMs);
}

// Plyn/brzda dle odchylky od cilove rychlosti (tri pasma kolem target +-1.5 m/s):
// pod pasmem plny plyn, nad pasmem brzda umerna prekroceni (clamp 0..1), v pasmu
// jemny plyn 0.4. speed je se znamenkem (couvani je < target -> plyn).
export function throttleBrakeFor(speed, targetSpeed) {
  if (speed < targetSpeed - 1.5) return { throttle: 1, brake: 0 };
  if (speed > targetSpeed + 1.5) return { throttle: 0, brake: clamp((speed - targetSpeed) * 0.25, 0, 1) };
  return { throttle: 0.4, brake: 0 };
}

// Rejd k cili: rozdil cilového a aktualniho smeru (uhel, rad), zabaleny do
// [-PI,PI], skalovany 1.8 a orezany do [-1,1]. Konvence: +1 = vpravo (proto negace
// rozdilu, ktery je v matematicke orientaci kladny vlevo). fwdAngle/targetAngle
// jsou atan2(x,z) smeru auta resp. k cili.
export function steerTo(fwdAngle, targetAngle) {
  let dAng = targetAngle - fwdAngle;
  while (dAng > Math.PI) dAng -= Math.PI * 2;
  while (dAng < -Math.PI) dAng += Math.PI * 2;
  return clamp(-dAng * 1.8, -1, 1);
}
